/**
 * StaffSheetsModal — extracted from Chat.jsx (2026-02-28) for maintainability.
 * Reads uploader-scoped ledger rows from Google Sheets, allows admin to tick
 * rows off / mark complaints. Business logic is UNCHANGED — pure code move.
 *
 * Public API (props):
 *   - sheets: [{ url, tab }]  — sheet metadata from `/api/groups/{id}`
 *   - groupName: string       — for header display
 *   - groupId: string         — used in mark-done / complaint API paths
 *   - onClose: fn             — close handler
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Copy, X, Loader2, CheckCircle2, Circle, XCircle, ArrowLeft, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { copyForSpreadsheet } from "@/lib/clipboard";

// Local copy of `displayLpgId` (also lives in Chat.jsx). Trims the padded
// 17-digit BharatGas form (`10000000<9digits>`) back to the 9-digit form
// the dealer portal shows. Kept as a local helper because it's the only
// place StaffSheetsModal needs it — no cross-file import risk.
function displayLpgId(lpg) {
  if (!lpg || typeof lpg !== "string") return lpg || "";
  if (/^10000000\d{9}$/.test(lpg)) {
    return lpg.slice(8).replace(/^0+/, "") || "0";
  }
  return lpg;
}

function StaffSheetsModal({ sheets, groupName, groupId, onClose }) {
  const [embedSheet, setEmbedSheet] = useState(null);
  const [dateMode, setDateMode] = useState("today");
  const [customDate, setCustomDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(() => new Set());
  const [complaining, setComplaining] = useState(() => new Set());
  const [complaintToast, setComplaintToast] = useState(null); // {type, text}
  const [copyStatus, setCopyStatus] = useState("");
  const [copiedCell, setCopiedCell] = useState(""); // `${rowKey}::lpg|mobile|dac`
  const datePickerRef = React.useRef(null);

  // Convert YYYY-MM-DD (native date input) → DD.MM.YYYY (sheet tab format).
  const isoToTabDate = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };
  // Convert DD.MM.YYYY → YYYY-MM-DD so the native picker shows current value.
  const tabDateToIso = (tab) => {
    if (!tab || !/^\d{2}\.\d{2}\.\d{4}$/.test(tab)) return "";
    const [d, m, y] = tab.split(".");
    return `${y}-${m}-${d}`;
  };
  // Click "Pick Date" → switch mode AND auto-open the native calendar.
  const openDatePicker = () => {
    setDateMode("custom");
    setTimeout(() => {
      const el = datePickerRef.current;
      if (!el) return;
      try {
        if (typeof el.showPicker === "function") el.showPicker();
        else el.focus();
      } catch (_e) {
        el.focus();
      }
    }, 50);
  };

  // One-click cell-level copy. Writes via the same `copyForSpreadsheet`
  // helper so 16-digit LPG IDs paste as TEXT into Excel / Google Sheets
  // (no scientific notation). Shows a brief "Copied!" badge on that cell.
  const copyCell = async (row, field, value) => {
    if (!value) return;
    const key = `${rowKey(row)}::${field}`;
    const ok = await copyForSpreadsheet([[String(value)]]);
    if (ok) {
      setCopiedCell(key);
      setTimeout(() => setCopiedCell((c) => (c === key ? "" : c)), 1500);
    }
  };

  // Today / yesterday in DD.MM.YYYY format — matches the staff Excel tab
  // naming convention (e.g. "07.06.2026").
  const today = useMemo(() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  }, []);
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  }, []);

  const activeTab = React.useMemo(() => {
    if (dateMode === "today") return today;
    if (dateMode === "old") return yesterday;
    if (dateMode === "custom") return customDate.trim();
    return "";
  }, [dateMode, today, yesterday, customDate]);

  // Last 7 days as quick-jump chips (skip today + yesterday — already
  // covered by their own buttons). Each chip shows just the day number
  // (e.g. "17") and sets the full DD.MM.YYYY behind the scenes.
  const recentDays = React.useMemo(() => {
    const out = [];
    for (let i = 2; i <= 8; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const tab = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
      out.push({ day: String(d.getDate()).padStart(2, "0"), tab });
    }
    return out;
  }, []);

  // Clear selection whenever the tab or sheet changes — keeps the UI sane.
  useEffect(() => {
    setSelected(new Set());
  }, [embedSheet, activeTab]);

  // Fetch the safe per-agency rows from the backend proxy whenever an
  // embedded sheet is open or the date selector changes. Refresh every
  // 60 seconds for "real-time" feel. ALSO listens to a global
  // `delivery.confirmed` event (dispatched from the chat WebSocket) and
  // patches the affected row in-place — admins see ticks light up
  // instantly, agencies see other staff's ticks the moment they hit the
  // wire.
  useEffect(() => {
    if (!embedSheet || !activeTab) {
      setRows([]);
      setErr("");
      return undefined;
    }
    let cancelled = false;
    const fetchRows = async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get(
          `/groups/${groupId}/staff-sheets/${embedSheet.id}/data`,
          { params: { tab: activeTab } },
        );
        if (!cancelled) setRows(data.rows || []);
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setErr(formatApiError(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRows();
    const t = setInterval(fetchRows, 60000);

    const onConfirm = (ev) => {
      const d = ev.detail || {};
      if (
        d.group_id !== groupId ||
        d.sheet_id !== embedSheet.id
      ) return;
      setRows((prev) =>
        prev.map((r) =>
          r.lpg_id === d.lpg_id && r.date === d.date
            ? {
                ...r,
                confirmed: !!d.confirmed,
                confirmed_by: d.confirmed_by || "",
                confirmed_at: d.confirmed_at || "",
              }
            : r,
        ),
      );
    };
    window.addEventListener("delivery.confirmed", onConfirm);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("delivery.confirmed", onConfirm);
    };
  }, [embedSheet, activeTab, groupId]);

  // Toggle a delivery confirmation. Optimistic — we patch the row immediately
  // and rely on the server response (or WS broadcast) to settle.
  const toggleConfirm = async (row) => {
    if (!embedSheet) return;
    const key = `${row.date}::${row.lpg_id}`;
    if (confirming.has(key)) return;
    setConfirming((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    // Optimistic flip
    const optimisticConfirmed = !row.confirmed;
    setRows((prev) =>
      prev.map((r) =>
        r === row
          ? { ...r, confirmed: optimisticConfirmed }
          : r,
      ),
    );
    try {
      const { data } = await api.post(
        `/groups/${groupId}/staff-sheets/${embedSheet.id}/confirm`,
        {
          date: row.date,
          lpg_id: row.lpg_id,
          mobile: row.mobile || "",
          dac: row.dac || "",
        },
      );
      setRows((prev) =>
        prev.map((r) =>
          r.date === row.date && r.lpg_id === row.lpg_id
            ? {
                ...r,
                confirmed: !!data.confirmed,
                confirmed_by: data.confirmed_by || "",
                confirmed_at: data.confirmed_at || "",
              }
            : r,
        ),
      );
    } catch (e) {
      // Rollback
      setRows((prev) =>
        prev.map((r) =>
          r.date === row.date && r.lpg_id === row.lpg_id
            ? { ...r, confirmed: row.confirmed }
            : r,
        ),
      );
      setErr(formatApiError(e));
    } finally {
      setConfirming((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Raise a complaint for a single staff-sheet row whose DAC came wrong.
  // Re-uses the existing bulk-complaint endpoint with a one-element array
  // so the same email + sheet-sync side-effects fire as a manual chat
  // mark-as-wrong. Backend returns one of three buckets:
  //   • complaint_created   — newly marked wrong (toast: success)
  //   • already_complaint   — was already wrong (toast: info)
  //   • not_dispatched      — LPG never sent in chat (toast: warning,
  //                            agency must paste it first then complain)
  const reportWrongDac = async (row) => {
    if (!row || !row.lpg_id) return;
    const ok = window.confirm(
      `Mark this DAC as WRONG?\n\nLPG: ${row.lpg_id}\nDAC: ${row.dac || "—"}\n\n` +
      `Admin ko email + Complaint Sheet me row add ho jayega. Confirm?`
    );
    if (!ok) return;
    const key = rowKey(row);
    setComplaining((prev) => new Set(prev).add(key));
    setComplaintToast(null);
    try {
      const { data } = await api.post(
        `/groups/${groupId}/lpg/bulk-complaint`,
        { lpg_ids: [row.lpg_id] }
      );
      const created = (data?.complaint_created || []).length;
      const already = (data?.already_complaint || []).length;
      const missing = (data?.not_dispatched || []).length;
      let toast;
      if (created) {
        toast = { type: "ok", text: `Complaint filed · admin notified (LPG ${displayLpgId(row.lpg_id)})` };
      } else if (already) {
        toast = { type: "info", text: `Pehle se complaint registered hai (LPG ${displayLpgId(row.lpg_id)})` };
      } else if (missing) {
        toast = { type: "warn", text: `Ye LPG abhi chat me dispatch nahi hua — pehle Bulk Paste se bhejo, fir complain.` };
      } else {
        toast = { type: "warn", text: "Kuch result nahi mila — try again." };
      }
      setComplaintToast(toast);
      setTimeout(() => setComplaintToast(null), 4500);
    } catch (e) {
      setComplaintToast({ type: "err", text: formatApiError(e) });
      setTimeout(() => setComplaintToast(null), 4500);
    } finally {
      setComplaining((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const rowKey = (r) => `${r.date}::${r.lpg_id}`;
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map(rowKey)));
  };
  const toggleRowSelected = (r) => {
    const k = rowKey(r);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const doCopy = async () => {
    const picked = rows.filter((r) => selected.has(rowKey(r)));
    if (!picked.length) {
      setCopyStatus("Pehle kuch rows tick karein.");
      setTimeout(() => setCopyStatus(""), 2500);
      return;
    }
    const matrix = [
      ["Date", "Agency", "LPG ID", "Mobile", "DAC", "Delivered"],
      ...picked.map((r) => [
        r.date,
        r.agency,
        displayLpgId(r.lpg_id),
        r.mobile || "",
        r.dac || "",
        r.confirmed ? "Yes" : "",
      ]),
    ];
    const ok = await copyForSpreadsheet(matrix);
    setCopyStatus(ok ? `Copied ${picked.length} rows · Excel/Sheet me paste karo` : "Copy fail");
    setTimeout(() => setCopyStatus(""), 3000);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
      onClick={onClose}
      data-testid="staff-sheets-modal"
    >
      <div
        className="bg-white rounded-lg w-full max-w-5xl border border-[#DCDCD3] shadow-xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#DCDCD3]">
          <div>
            <h2
              className="text-lg font-semibold text-[#1C1C19] flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8553D] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C8553D]"></span>
              </span>
              Real Time
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#6B6B61] ml-1">
                · {groupName}
              </span>
            </h2>
            <p className="text-xs text-[#6B6B61] mt-0.5">
              Live delivery — Date · LPG ID · Mobile · DAC only (URLs / panels hidden)
            </p>
          </div>
          <button onClick={onClose} className="text-[#6B6B61] hover:text-[#1C1C19] p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        {embedSheet ? (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-[#DCDCD3] bg-[#F5F5F0]">
              <button
                onClick={() => setEmbedSheet(null)}
                data-testid="staff-sheet-back"
                className="text-xs text-[#385C4B] hover:underline flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" /> All Sheets
              </button>
              <span className="text-sm font-semibold text-[#1C1C19] ml-2">
                {embedSheet.name}
              </span>
              <div
                className="ml-auto flex items-center gap-1 bg-white border border-[#DCDCD3] rounded p-0.5"
                data-testid="staff-sheet-date-toggle"
              >
                {[
                  { id: "today", label: `Today · ${today}` },
                  { id: "old", label: `Old · ${yesterday}` },
                  { id: "custom", label: customDate ? `📅 ${customDate}` : "📅 Pick Date" },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => (m.id === "custom" ? openDatePicker() : setDateMode(m.id))}
                    data-testid={`staff-sheet-date-${m.id}`}
                    className={`text-[11px] font-mono tracking-[0.1em] uppercase px-2 py-1 rounded ${
                      dateMode === m.id
                        ? "bg-[#385C4B] text-white"
                        : "text-[#6B6B61] hover:bg-[#F5F5F0]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {/* Quick-jump day chips: last 7 days (skipping today/yesterday).
                  Click any number to set that full DD.MM.YYYY date. */}
              <div
                className="flex items-center gap-1 bg-white border border-[#DCDCD3] rounded p-0.5"
                data-testid="staff-sheet-day-chips"
              >
                {recentDays.map((rd) => {
                  const active = dateMode === "custom" && customDate === rd.tab;
                  return (
                    <button
                      key={rd.tab}
                      onClick={() => {
                        setCustomDate(rd.tab);
                        setDateMode("custom");
                      }}
                      title={rd.tab}
                      data-testid={`staff-sheet-day-chip-${rd.day}`}
                      className={`text-[11px] font-mono tracking-[0.05em] px-2 py-1 rounded min-w-[26px] ${
                        active
                          ? "bg-[#385C4B] text-white"
                          : "text-[#6B6B61] hover:bg-[#F5F5F0]"
                      }`}
                    >
                      {rd.day}
                    </button>
                  );
                })}
              </div>
              {/* Native date picker — opens browser calendar on click.
                  Always rendered (visually hidden in non-custom mode) so the
                  ref is available and showPicker() works on first click. */}
              <input
                ref={datePickerRef}
                type="date"
                value={tabDateToIso(customDate)}
                onChange={(e) => {
                  const v = isoToTabDate(e.target.value);
                  setCustomDate(v);
                  if (v) setDateMode("custom");
                }}
                max={new Date().toISOString().slice(0, 10)}
                data-testid="staff-sheet-custom-date"
                className={`text-xs font-mono px-2 py-1 border border-[#DCDCD3] rounded focus:outline-none focus:border-[#385C4B] ${
                  dateMode === "custom" ? "block w-36" : "sr-only"
                }`}
                aria-label="Pick a date"
              />
              <span className="text-[10px] text-[#6B6B61] font-mono">
                {loading ? "Refreshing…" : `${rows.length} rows`}
              </span>
              {rows.length > 0 && (
                <button
                  onClick={doCopy}
                  disabled={selected.size === 0}
                  data-testid="staff-sheet-copy"
                  title={
                    selected.size > 0
                      ? `Copy ${selected.size} rows (Excel / Google Sheet me paste karne ke liye)`
                      : "Pehle kuch rows tick karein"
                  }
                  className={`text-[11px] font-mono tracking-[0.1em] uppercase px-2 py-1 rounded flex items-center gap-1.5 ${
                    selected.size > 0
                      ? "bg-[#385C4B] text-white hover:bg-[#2D4A3C]"
                      : "bg-[#DCDCD3] text-[#6B6B61] cursor-not-allowed"
                  }`}
                >
                  <Copy className="w-3 h-3" />
                  Copy {selected.size > 0 ? `(${selected.size})` : ""}
                </button>
              )}
              {copyStatus && (
                <span
                  className="text-[10px] font-mono text-[#385C4B]"
                  data-testid="staff-sheet-copy-status"
                >
                  {copyStatus}
                </span>
              )}
            </div>
            <div className="flex-1 overflow-auto" data-testid="staff-sheet-table-wrapper">
              {err && (
                <div
                  className="m-4 p-3 bg-red-500/10 text-red-700 text-xs rounded leading-relaxed"
                  data-testid="staff-sheet-error"
                >
                  {/Tab .* not found|Google served a different tab/i.test(err) ? (
                    <>
                      <b>{activeTab}</b> ka tab abhi is sheet me nahi banaya
                      gaya. Aapki staff team us date ka tab banayegi tab data
                      yahan aayega. <i>Old date toggle ya Pick Date se purani
                      date dekh sakte ho.</i>
                    </>
                  ) : (
                    err
                  )}
                </div>
              )}
              {!err && !loading && rows.length === 0 && (
                <div className="text-center text-sm text-[#6B6B61] py-12" data-testid="staff-sheet-empty">
                  <b>{activeTab}</b> par koi delivery data nahi mila.
                </div>
              )}
              {rows.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#F5F5F0] z-10">
                    <tr>
                      <th className="text-left px-2 py-2 border-b border-[#DCDCD3]" style={{ width: 28 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          data-testid="staff-sheet-select-all"
                          className="accent-[#385C4B] cursor-pointer"
                          title={allSelected ? "Deselect all" : "Select all"}
                        />
                      </th>
                      {["Date", "Agency", "LPG ID", "Mobile", "DAC", "Delivered"].map((h) => (
                        <th
                          key={h}
                          className="text-left px-3 py-2 text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] border-b border-[#DCDCD3]"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody data-testid="staff-sheet-rows">
                    {rows.map((r, i) => {
                      const k = rowKey(r);
                      const isSel = selected.has(k);
                      const isBusy = confirming.has(k);
                      const isComplainBusy = complaining.has(k);
                      return (
                        <tr
                          key={`${r.lpg_id}::${i}`}
                          className={`border-b border-[#DCDCD3]/40 ${
                            r.confirmed
                              ? "bg-emerald-500/5 hover:bg-emerald-500/10"
                              : isSel
                              ? "bg-[#385C4B]/5 hover:bg-[#385C4B]/10"
                              : "hover:bg-[#F5F5F0]/50"
                          }`}
                        >
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleRowSelected(r)}
                              data-testid="staff-sheet-row-select"
                              className="accent-[#385C4B] cursor-pointer"
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-[12px] text-[#6B6B61] whitespace-nowrap">
                            {r.date}
                          </td>
                          <td className="px-3 py-2 text-[12px] text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {r.agency}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => copyCell(r, "lpg", displayLpgId(r.lpg_id))}
                              data-testid="staff-sheet-cell-lpg"
                              title={`Click to copy ${displayLpgId(r.lpg_id)} · full ID: ${r.lpg_id}`}
                              className="font-mono text-[12px] text-[#1C1C19] font-semibold hover:bg-[#385C4B]/10 px-1 py-0.5 rounded cursor-pointer"
                            >
                              {displayLpgId(r.lpg_id)}
                              {copiedCell === `${rowKey(r)}::lpg` && (
                                <span className="ml-2 text-[10px] text-emerald-700">✓ Copied</span>
                              )}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            {r.mobile ? (
                              <button
                                type="button"
                                onClick={() => copyCell(r, "mobile", r.mobile)}
                                data-testid="staff-sheet-cell-mobile"
                                title="Click to copy"
                                className="font-mono text-[12px] text-[#1C1C19] hover:bg-[#385C4B]/10 px-1 py-0.5 rounded cursor-pointer"
                              >
                                {r.mobile}
                                {copiedCell === `${rowKey(r)}::mobile` && (
                                  <span className="ml-2 text-[10px] text-emerald-700">✓ Copied</span>
                                )}
                              </button>
                            ) : (
                              <span className="text-[#6B6B61]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {r.dac === "BOOKING DONE" ? (
                              // BharatGas SMS says only "BOOKING DONE" —
                              // delivery code not generated yet. Show a
                              // pill badge instead of a copyable DAC.
                              <span
                                data-testid="staff-sheet-cell-booking-done"
                                title="Cylinder booked but delivery code not generated yet"
                                className="inline-block font-mono text-[10px] tracking-[0.05em] uppercase font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded"
                              >
                                BOOKING DONE
                              </span>
                            ) : r.dac ? (
                              <button
                                type="button"
                                onClick={() => copyCell(r, "dac", r.dac)}
                                data-testid="staff-sheet-cell-dac"
                                title="Click to copy"
                                className="font-mono text-[12px] font-bold text-[#385C4B] hover:bg-[#385C4B]/15 px-1 py-0.5 rounded cursor-pointer"
                              >
                                {r.dac}
                                {copiedCell === `${rowKey(r)}::dac` && (
                                  <span className="ml-2 text-[10px] text-emerald-700">✓</span>
                                )}
                              </button>
                            ) : (
                              <span className="text-[#6B6B61]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => toggleConfirm(r)}
                                disabled={isBusy}
                                data-testid="staff-sheet-deliver-toggle"
                                title={
                                  r.confirmed
                                    ? `Delivered · by ${r.confirmed_by || "—"} (click to undo)`
                                    : "Mark as Delivered — admin instant dekh lega"
                                }
                                className={`px-2 py-1 rounded text-[10px] font-mono tracking-[0.1em] uppercase border transition-colors flex items-center gap-1 ${
                                  r.confirmed
                                    ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/40 hover:bg-emerald-500/25"
                                    : "bg-white text-[#6B6B61] border-[#DCDCD3] hover:border-[#385C4B] hover:text-[#385C4B]"
                                } ${isBusy ? "opacity-50 cursor-wait" : ""}`}
                              >
                                {isBusy ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : r.confirmed ? (
                                  <CheckCircle2 className="w-3 h-3" />
                                ) : (
                                  <Circle className="w-3 h-3" />
                                )}
                                {r.confirmed ? "Done" : "Tick"}
                              </button>
                              {/* WRONG DAC complaint — disabled for rows whose
                                  DAC is empty or BOOKING DONE (nothing to
                                  complain about). The endpoint will also
                                  return 'not_dispatched' if the LPG was never
                                  sent in chat — surfaced as a toast. */}
                              <button
                                onClick={() => reportWrongDac(r)}
                                disabled={isComplainBusy || !r.dac || r.dac === "BOOKING DONE"}
                                data-testid="staff-sheet-complain"
                                title={
                                  !r.dac
                                    ? "Koi DAC nahi — complaint nahi kar sakte"
                                    : r.dac === "BOOKING DONE"
                                    ? "Abhi sirf booking hai, DAC generate nahi hua — complaint applicable nahi"
                                    : `Mark DAC ${r.dac} as WRONG — admin ko email + Complaint Sheet me row add hoga`
                                }
                                className={`px-2 py-1 rounded text-[10px] font-mono tracking-[0.1em] uppercase border transition-colors flex items-center gap-1 ${
                                  !r.dac || r.dac === "BOOKING DONE"
                                    ? "bg-[#F5F5F0] text-[#A7A79D] border-[#DCDCD3] cursor-not-allowed"
                                    : "bg-white text-red-700 border-red-300 hover:bg-red-500/10 hover:border-red-500"
                                } ${isComplainBusy ? "opacity-50 cursor-wait" : ""}`}
                              >
                                {isComplainBusy ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <XCircle className="w-3 h-3" />
                                )}
                                Wrong
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-4 py-2 text-[10px] text-[#6B6B61] border-t border-[#DCDCD3] bg-[#F5F5F0] leading-relaxed">
              Auto-refresh har 60s · <b>LPG / Mobile / DAC</b> cell pe click
              karke single value copy · checkboxes select karke{" "}
              <b>Copy (N)</b> se bulk copy (Excel / Sheet me paste karo) ·{" "}
              <b>Tick</b> dabaake delivery confirm karo — admin instant
              dekh lega · <b className="text-red-700">Wrong</b> dabaake galat
              DAC ki complaint raise karo (admin ko email + Complaint Sheet me
              row add ho jayega).
            </div>
            {complaintToast && (
              <div
                data-testid="staff-sheet-complaint-toast"
                className={`fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 rounded-lg shadow-lg border text-sm font-medium ${
                  complaintToast.type === "ok"
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                    : complaintToast.type === "info"
                    ? "bg-blue-50 text-blue-800 border-blue-300"
                    : complaintToast.type === "warn"
                    ? "bg-amber-50 text-amber-800 border-amber-300"
                    : "bg-red-50 text-red-800 border-red-300"
                }`}
              >
                {complaintToast.text}
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-2 overflow-y-auto">
            {sheets.length === 0 && (
              <div className="text-sm text-[#6B6B61] text-center py-10">
                Koi real-time sheet abhi tak add nahi kiya gaya hai. Admin se kahein.
              </div>
            )}
            {sheets.map((s) => (
              <div
                key={s.id}
                data-testid="staff-sheet-row"
                className="bg-[#F5F5F0] border border-[#DCDCD3] rounded-md p-3 flex items-center gap-3"
              >
                <div className="p-2 bg-[#385C4B]/10 text-[#385C4B] rounded">
                  <FileSpreadsheet className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-semibold text-[#1C1C19]"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {s.name}
                  </div>
                  <div className="text-[10px] font-mono text-[#6B6B61]">
                    Safe view · 5 columns only (Date / Agency / LPG / Mobile / DAC)
                  </div>
                </div>
                <button
                  onClick={() => { setDateMode("today"); setEmbedSheet(s); }}
                  data-testid="staff-sheet-view-button"
                  className="px-3 py-1.5 text-xs font-mono tracking-[0.15em] uppercase bg-[#385C4B] text-white rounded hover:bg-[#2D4A3C] flex-shrink-0"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffSheetsModal;
